/**
 * MarkdownRenderer
 *
 * Small, focused wrapper around `react-markdown`.
 * Used by:
 * - Admin Articles preview
 * - Public Guides article page
 *
 * Notes:
 * - We intentionally do not allow raw HTML. `react-markdown` does not render raw
 *   HTML unless `rehypeRaw` is added (we don't add it).
 * - Content is authored by the owner/admin, but keeping this conservative is a
 *   good default.
 */

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

type Props = {
  markdown: string;
  className?: string;
};

export default function MarkdownRenderer({ markdown, className }: Props) {
  return (
    <div className={className}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: (props) => <h1 className="text-3xl font-bold text-gray-900 mt-6 mb-3" {...props} />,
          h2: (props) => <h2 className="text-2xl font-semibold text-gray-900 mt-6 mb-3" {...props} />,
          h3: (props) => <h3 className="text-xl font-semibold text-gray-900 mt-5 mb-2" {...props} />,
          p: (props) => <p className="text-gray-700 leading-7 my-3" {...props} />,
          ul: (props) => <ul className="list-disc pl-6 my-3 text-gray-700" {...props} />,
          ol: (props) => <ol className="list-decimal pl-6 my-3 text-gray-700" {...props} />,
          li: (props) => <li className="my-1" {...props} />,
          a: (props) => (
            <a
              className="text-purple-700 underline hover:text-purple-800"
              target="_blank"
              rel="noopener noreferrer"
              {...props}
            />
          ),
          blockquote: (props) => (
            <blockquote className="border-l-4 border-purple-200 pl-4 py-1 my-4 text-gray-700" {...props} />
          ),
          code: (props) => (
            <code className="bg-gray-100 text-gray-800 px-1 py-0.5 rounded text-sm" {...props} />
          ),
          pre: (props) => (
            <pre className="bg-gray-900 text-gray-100 p-4 rounded-xl overflow-auto my-4 text-sm" {...props} />
          ),
        }}
      >
        {markdown || ''}
      </ReactMarkdown>
    </div>
  );
}


